import { useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import InterventionTab from '../InterventionTab';
import SimulationOutput from './SimulationOutput';
import { api } from '../../api/client';
import type { SectorShockResponse } from '../../types';
import { CodeText } from '../Code';

const SUPPLIERS: Array<{ id: string; label: string; type: string }> = [
  { id: 'vobev',             label: 'Vobev (cans, primary)',        type: 'can_manufacturer' },
  { id: 'ball',              label: 'Ball Corp (cans)',             type: 'can_manufacturer' },
  { id: 'crown',             label: 'Crown Holdings (cans)',        type: 'can_manufacturer' },
  { id: 'tate_lyle',         label: 'Tate & Lyle (sweetener)',      type: 'sweetener_refiner' },
  { id: 'adm',               label: 'ADM (sweetener)',              type: 'sweetener_refiner' },
  { id: 'cargill',           label: 'Cargill (sweetener)',          type: 'sweetener_refiner' },
  { id: 'trinity_logistics', label: 'Trinity Logistics (freight)',  type: 'freight' },
];

export default function SectorShockConsole() {
  const [supplierId, setSupplierId] = useState('vobev');
  const [severity, setSeverity] = useState(1.0);

  const run = useMutation({
    mutationFn: () => api.sectorShockRun(supplierId, severity),
  });

  return (
    <InterventionTab
      objective="O3 + O4 · stress-test shared-supplier concentration risk"
      workflow={[
        'Pick a supplier (cans / sweetener / freight) and a severity (0–100%)',
        'Bipartite supplier-operator graph computes displaced capacity per operator',
        'Greedy LP allocates volume to alternative suppliers respecting capacity + lead time',
        'Report which operator\'s binding constraint breaks first + exposed assumption',
      ]}
      decisionRules={{
        rules: [
          'Activate dual-sourcing plan if single-supplier share > 55% AND unfulfilled ratio > 30%',
          'Escalate to spine refresh if removed supplier maps to A1m / A2m / A3m assumption',
        ],
      }}
      aiLogic={{
        description: 'Deterministic bipartite cascade: removed supplier → operator displacement → greedy alternative allocation. No randomness; same input → byte-identical output.',
        modality: 'deterministic',
        hitlLevel: 'L4 human decision',
      }}
      kpi={{
        primary: 'first_to_break operator · unfulfilled ratio · mitigations ranked by cost-effectiveness',
        downstream: 'Feeds Profit-at-Risk single_supplier_share + procurement dual-sourcing plan',
        owner: 'CPO / Supply Chain · escalates to Strategy if exposes A-series assumption',
      }}
      content={
        <div className="panel">
          <div className="qhead">
            <span className="qdot" style={{ background: 'var(--red)' }} />
            Remove a node · propagate cascade
          </div>

          <div className="row" style={{ gap: 14, marginBottom: 16, alignItems: 'flex-end' }}>
            <div>
              <div className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Supplier to remove</div>
              <select className="c2-input" style={{ width: 280 }} value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
                {SUPPLIERS.map((s) => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="row" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
                <span className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Severity</span>
                <span className="mono" style={{ fontSize: 13, color: 'var(--red)', fontWeight: 600 }}>{(severity * 100).toFixed(0)}%</span>
              </div>
              <input className="slider" type="range" min={0} max={1} step={0.05} value={severity} onChange={(e) => setSeverity(parseFloat(e.target.value))} style={{ width: 220 }} />
            </div>
            <button type="button" className="btn primary" onClick={() => run.mutate()} disabled={run.isPending}>
              {run.isPending ? 'Simulating…' : '⚡ Run shock'}
            </button>
          </div>

          {run.data && <SectorShockResult data={run.data} />}
        </div>
      }
    />
  );
}

function SectorShockResult({ data }: { data: SectorShockResponse }) {
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
        <Stat label="First to break" value={data.first_to_break} color="red" />
        <Stat label="Exposed assumption" value={<CodeText text={data.exposed_assumption} compact />} color="gold" />
        <Stat label="Binding shifts" value={<CodeText text={`${data.binding_constraint_shift.from} → ${data.binding_constraint_shift.to}`} compact />} color="olive" />
      </div>

      <div className="panel" style={{ background: 'var(--panel2)', marginBottom: 14 }}>
        <div className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
          Operator impacts · cascade through bipartite graph
        </div>
        <table className="c2-table" style={{ width: '100%', fontSize: 13 }}>
          <thead>
            <tr>
              <th align="left">Operator</th>
              <th align="right">Displaced</th>
              <th align="right">Fulfilled</th>
              <th align="right">Unfulfilled</th>
              <th align="right">Ratio</th>
              <th align="right">Lead time</th>
              <th align="right">$Δ</th>
            </tr>
          </thead>
          <tbody>
            {data.operator_impacts.map((o) => (
              <tr key={o.operator_id} style={{ background: o.operator_id === data.first_to_break ? 'rgba(207,84,64,0.08)' : 'transparent' }}>
                <td><b className="mono">{o.operator_id}</b> · {o.operator_name}</td>
                <td align="right" className="mono">{o.displaced_volume.toFixed(0)}</td>
                <td align="right" className="mono">{o.fulfilled_volume.toFixed(0)}</td>
                <td align="right" className="mono" style={{ color: o.unfulfilled_volume > 0 ? 'var(--red)' : 'var(--muted)' }}>
                  {o.unfulfilled_volume.toFixed(0)}
                </td>
                <td align="right" className="mono" style={{ color: o.unfulfilled_ratio > 0.3 ? 'var(--red)' : o.unfulfilled_ratio > 0 ? 'var(--gold)' : 'var(--muted)' }}>
                  {(o.unfulfilled_ratio * 100).toFixed(1)}%
                </td>
                <td align="right" className="mono">{o.lead_time.toFixed(0)}w</td>
                <td align="right" className="mono">${o.cost_delta.toFixed(0)}K</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="panel" style={{ background: 'var(--panel2)', marginBottom: 14 }}>
        <div className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
          Mitigations · ranked by cost-effectiveness
        </div>
        {data.mitigations.map((m) => (
          <div key={m.rank} className="row" style={{ justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--line-soft)', fontSize: 13 }}>
            <div>
              <span className="tag muted" style={{ marginRight: 8 }}>#{m.rank}</span>
              {m.description}
            </div>
            <div className="row" style={{ gap: 14 }}>
              {m.alternative_supplier && <span className="mono small">alt: {m.alternative_supplier}</span>}
              <span className="mono small">lead {m.lead_time.toFixed(0)}w</span>
              <span className="mono small" style={{ color: 'var(--olive)' }}>cost-eff {m.cost_effectiveness.toFixed(2)}</span>
            </div>
          </div>
        ))}
      </div>

      {/* 4-part Move Brief */}
      <div style={{ marginTop: 14 }}>
        <SectorShockMoveBrief data={data} />
      </div>
    </>
  );
}

function SectorShockMoveBrief({ data }: { data: SectorShockResponse }) {
  const decideBy = new Date(Date.now() + 30 * 86400 * 1000).toISOString().slice(0, 10);
  const breakingOp = data.operator_impacts.find((o) => o.operator_id === data.first_to_break);
  const topMitigation = data.mitigations[0];
  const recommendation = topMitigation
    ? topMitigation.description
    : `Qualify alternative supplier for ${data.removed_supplier.supplier_id} within 18 weeks.`;
  const recommendationDetail = breakingOp
    ? `${breakingOp.operator_name} carries ${breakingOp.unfulfilled_volume.toFixed(0)} ${data.removed_supplier.capacity_unit} of unfulfilled volume (${(breakingOp.unfulfilled_ratio * 100).toFixed(1)}% of total) at ${breakingOp.lead_time.toFixed(0)} weeks lead time to qualified alternatives.`
    : 'No operator breaks in the current shock; mitigation is precautionary.';

  return (
    <SimulationOutput
      onDispose={async (d, _driftId, decision) => {
        await api.simulationDisposition({
          simulation_kind: 'sector_shock',
          signal_id: 'S-04',  // retailer concentration / structural risk
          disposition: d,
          owner: 'CPO / Supply Chain',
          recommendation,
          predicted_outcome: {
            removed_supplier: data.removed_supplier.supplier_id,
            first_to_break: data.first_to_break,
            exposed_assumption: data.exposed_assumption,
            top_mitigation_cost_effectiveness: topMitigation?.cost_effectiveness ?? null,
          },
          decision,
        });
      }}
      recommendation={recommendation}
      recommendationDetail={recommendationDetail}
      evidence={{
        headline: `Removing ${data.removed_supplier.supplier_id} at severity ${(data.removed_supplier.severity * 100).toFixed(0)}% exposes ${data.exposed_assumption}. ${data.first_to_break} breaks first.`,
        bindingConstraintShift: { from: data.binding_constraint_shift.from, to: data.binding_constraint_shift.to },
        exposedAssumption: data.binding_constraint_shift.reason ?? `Exposes ${data.exposed_assumption} — re-validate sourcing assumption next spine refresh.`,
        metrics: [
          { label: 'Removed capacity', value: `${data.removed_supplier.capacity_removed.toFixed(0)} ${data.removed_supplier.capacity_unit}`, color: 'red' },
          { label: 'Unfulfilled (first to break)', value: breakingOp ? `${breakingOp.unfulfilled_volume.toFixed(0)}` : '0', color: 'red' },
          { label: 'Lead time to alt', value: topMitigation ? `${topMitigation.lead_time.toFixed(0)}w` : 'n/a', color: 'gold' },
          { label: 'Alt suppliers', value: data.removed_supplier.alternatives.length.toString(), color: 'olive' },
        ],
      }}
      alternatives={[
        {
          rank: 2,
          move: `Stay sole-sourced on ${data.removed_supplier.supplier_id}`,
          expected: breakingOp ? `Full ${breakingOp.unfulfilled_volume.toFixed(0)} unfulfilled exposure if shock materializes` : 'no immediate impact',
          whyLower: 'Leaves the binding constraint on a single supplier; no time-to-recover',
        },
        ...(data.mitigations.slice(1, 2).map((m) => ({
          rank: 3 as const,
          move: m.description,
          expected: `Lead time ${m.lead_time.toFixed(0)}w · cost-effectiveness ${m.cost_effectiveness.toFixed(2)}`,
          whyLower: 'Lower cost-effectiveness than the recommended path',
        }))),
      ]}
      actionPath={{
        owner: 'CPO / Supply Chain · escalate to Strategy on A1m/A2m/A3m exposure',
        decideBy,
        tripwire: `${data.removed_supplier.supplier_id} capacity-disruption signal fires OR alt-supplier qualification slips > 4 weeks`,
        rollback: 'Re-concentrate to primary supplier within 2 weeks; absorb premium until spine refresh',
      }}
    />
  );
}

function Stat({ label, value, color }: { label: string; value: ReactNode; color: 'olive' | 'red' | 'gold' | 'muted' }) {
  const c = { olive: 'var(--olive)', red: 'var(--red)', gold: 'var(--gold)', muted: 'var(--muted)' }[color];
  return (
    <div className="panel" style={{ padding: 14, background: 'var(--panel2)' }}>
      <div className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{label}</div>
      <div className="num" style={{ color: c, fontSize: 18 }}>{value}</div>
    </div>
  );
}
